import { useEffect, useMemo, useState } from "react";
import { useData } from "@core/lib/useData";
import { useViewState } from "@core/lib/useViewState";
import LazyMap, { type Basemap, type MapPoint } from "@core/components/LazyMap";
import GroupFilter from "@core/components/GroupFilter";
import TrendChart, { type Series } from "@core/components/TrendChart";
import ShareCard from "@core/components/ShareCard";
import { Footer, Header, Splash, Tabs, type Pane } from "@core/components/AppShell";
import WaterWatchCredit from "@core/components/WaterWatchCredit";
import InfoButton from "@core/components/InfoDialog";
import { ABOUT } from "./lib/about";
import { formatValue, mean, plural } from "@core/lib/format";
import StationList from "./components/StationList";
import StationDetail from "./components/StationDetail";
import { DEFAULT_PARAM, PARAMS, PARAM_BY_KEY, classify, scaleFor, terciles as tercilesOf } from "./lib/params";
import { DEPTHS, DEPTH_COLOR, type Depth, type Payload } from "./lib/types";

const RATINGS = ["good", "fair", "poor", "none"] as const;

export default function App() {
  const { data, error, loading, refreshing, lastRefresh, refresh } = useData<Payload>();
  const view = useViewState("waterbody");
  const [basemap, setBasemap] = useState<Basemap>("satellite");
  const [pane, setPane] = useState<Pane>("map");

  const param = PARAM_BY_KEY.get(view.params.get("param") ?? "")
    ?? PARAM_BY_KEY.get(DEFAULT_PARAM)!;
  /*
   * Which depth classes are plotted. All three by default: surface, middle
   * and bottom are physically different water, and seeing them together is
   * the point — stratification is what a depth profile is for. Each can be
   * switched off from the chart legend, and the set rides in the URL so a
   * link can pin a particular comparison.
   *
   * Only the chart is affected. Map colouring always uses the station's most
   * recent surface value, so the two cannot drift apart.
   */
  const depthParam = view.params.get("depths");
  const depths: Depth[] = (() => {
    if (depthParam === null) return DEPTHS;
    if (depthParam === "") return [];
    const want = new Set(depthParam.split(",").map((d) => d.trim().toLowerCase()));
    return DEPTHS.filter((d) => want.has(d.toLowerCase()));
  })();

  const toggleDepth = (d: Depth) => {
    const turningOff = depths.includes(d);
    // Refuse to hide the last visible layer. An empty chart is not a state
    // anyone wants, and the empty string would be stripped from the query by
    // setParam and read back as the default — switching all three on again,
    // which looks like the button doing the opposite of what it says.
    if (turningOff && depths.length === 1) return;

    const next = turningOff ? depths.filter((x) => x !== d) : [...depths, d];
    const ordered = DEPTHS.filter((x) => next.includes(x));
    // The default (all three) is the absent parameter, keeping shared links
    // clean for the common case.
    view.setParam(
      "depths",
      ordered.length === DEPTHS.length ? null : ordered.join(","),
    );
  };

  useEffect(() => {
    if (view.stationId && window.innerWidth <= 1060) setPane("map");
  }, [view.stationId]);

  const selected = useMemo(
    () => data?.stations.find((s) => s.key === view.stationId) ?? null,
    [data, view.stationId],
  );
  const activeWb = view.group ?? null;

  const inScope = useMemo(() => {
    if (!data) return [];
    return activeWb ? data.stations.filter((s) => s.waterbody === activeWb) : data.stations;
  }, [data, activeWb]);

  /*
   * Terciles for relative parameters.
   *
   * The scale is re-based to the stations in scope where that is meaningful,
   * so a waterbody is ranked against itself. But no waterbody here has more
   * than five stations — Walloon Lake is the largest — and three buckets cut
   * from five values says nothing. Rather than show every station grey, fall
   * back to ranking against all 66 and say which basis is in use, because
   * "upper third of this lake" and "upper third of the region" are different
   * claims and the reader has to know which one they are being shown.
   */
  const tercileInfo = useMemo(() => {
    if (!data || param.judgement !== "relative") return null;
    const pick = (list: typeof inScope) =>
      list.map((s) => s.latest[param.key]?.v).filter((v): v is number => v !== undefined);

    const scoped = tercilesOf(pick(inScope));
    if (scoped) return { cuts: scoped, basis: "scope" as const, n: pick(inScope).length };

    const all = tercilesOf(pick(data.stations));
    return all ? { cuts: all, basis: "all" as const, n: pick(data.stations).length } : null;
  }, [data, inScope, param]);

  const terciles = tercileInfo?.cuts ?? null;

  const rowsInScope = useMemo(() => {
    if (!data) return [];
    const base = selected
      ? data.samples.filter((r) => r.s === selected.key)
      : (() => {
        const ids = new Set(inScope.map((s) => s.key));
        return data.samples.filter((r) => ids.has(r.s));
      })();
    // Deliberately not filtered by depth: the legend has to show a toggle for
    // every depth this station actually has, including the ones switched off.
    return base;
  }, [data, selected, inScope]);

  const points: MapPoint[] = useMemo(() => {
    if (!data) return [];
    const scope = new Set(inScope.map((s) => s.key));
    const scale = scaleFor(param);
    return data.stations.map((s) => {
      const rating = classify(param, s.latest[param.key]?.v, terciles);
      return {
        id: s.key,
        lat: s.lat,
        lng: s.lng,
        color: scale[rating].color,
        label: `${s.siteId} — ${s.name} (${param.short}: ${
          formatValue(s.latest[param.key]?.v ?? null, param.digits)
        }${param.unit ? " " + param.unit : ""})`,
        muted: !scope.has(s.key),
      };
    });
  }, [data, inScope, param, terciles]);

  const fitTo = useMemo(() => points.filter((p) => !p.muted), [points]);

  if (loading) return <Splash>Loading water quality data…</Splash>;
  if (error || !data) {
    return (
      <Splash tone="error">
        <strong>Could not load water quality data.</strong>
        <span>{error}</span>
        <button className="btn" onClick={refresh}>Try again</button>
      </Splash>
    );
  }

  const scale = scaleFor(param);
  const legend = RATINGS
    .map((r) => ({
      key: r,
      label: scale[r].label,
      color: scale[r].color,
      n: inScope.filter((s) => classify(param, s.latest[param.key]?.v, terciles) === r).length,
    }))
    .filter((l) => l.n > 0);

  /*
   * One series per depth class. This is the shape the council's existing
   * dashboard uses, and it is the right one: surface, middle and bottom are
   * physically different water, not noise to be averaged together.
   */
  const allSeries: Series[] = DEPTHS.map((d) => {
    const rows = rowsInScope.filter((r) => r.c === d && r.v[param.key] !== undefined);
    if (selected) {
      return {
        key: d, label: d, color: DEPTH_COLOR[d], kind: "line" as const,
        points: rows
          .map((r) => ({ x: r.y, y: r.v[param.key], note: r.d }))
          .sort((a, b) => a.x - b.x),
      };
    }
    // Across many stations, the yearly mean per depth; the individual points
    // would be an unreadable cloud at this scale.
    const byYear = new Map<number, number[]>();
    for (const r of rows) (byYear.get(r.y) ?? byYear.set(r.y, []).get(r.y)!).push(r.v[param.key]);
    return {
      key: d, label: d, color: DEPTH_COLOR[d], kind: "line" as const,
      points: [...byYear.entries()]
        .map(([y, vs]) => ({ x: y, y: mean(vs) as number, note: `${d} · mean of ${plural(vs.length, "sample")}` }))
        .sort((a, b) => a.x - b.x),
    };
  });

  /** Depths this station/waterbody actually has data for at this parameter. */
  const available = allSeries.filter((s) => s.points.length > 0).map((s) => s.key as Depth);
  const series = allSeries.filter(
    (s) => s.points.length > 0 && depths.includes(s.key as Depth),
  );

  const thresholds = (() => {
    if (param.judgement === "threshold" && param.cuts) {
      return param.cuts.map((v, i) => ({
        value: v,
        label: `${v}${param.unit ? " " + param.unit : ""}`,
        color: i === param.cuts!.length - 1 ? "#A4161A" : "#C99700",
      }));
    }
    if (param.judgement === "band" && param.range) {
      return param.range.map((v) => ({
        value: v, label: `pH ${v}`, color: "#C99700",
      }));
    }
    return [];
  })();

  const scopeName = activeWb ?? null;
  const chartTitle = selected
    ? `${selected.siteId} — ${param.label.toLowerCase()}`
    : `${scopeName ?? "All waterbodies"} — average ${param.label.toLowerCase()} by year`;
  const pointCount = series.reduce((a, s) => a + s.points.length, 0);

  return (
    <div className="app">
      <Header
        program="Comprehensive Water Quality Monitoring"
        programShort="Water Quality"
        legend={legend}
        basemap={basemap}
        onBasemap={setBasemap}
        refreshing={refreshing}
        lastRefresh={lastRefresh}
        onRefresh={refresh}
        extra={<InfoButton content={ABOUT} />}
      />

      <div className="subbar">
        <GroupFilter
          noun="Waterbody"
          nounPlural="Waterbodies"
          options={data.waterbodies}
          value={activeWb}
          onChange={view.selectGroup}
          link={activeWb ? view.groupLink(activeWb) : null}
        />
        <label className="wsfilter">
          <span>Parameter</span>
          <select
            value={param.key}
            onChange={(e) => view.setParam("param", e.target.value === DEFAULT_PARAM ? null : e.target.value)}
          >
            {PARAMS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}{p.unit ? ` (${p.unit})` : ""} — {data.coverage[p.key] ?? 0} samples
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <button className="btn ghost sm" onClick={() => view.selectStation(null)}>
            Clear station
          </button>
        )}
        <span className="subbar-spacer" />
        <span className="subbar-note">
          {scopeName
            ? `${plural(inScope.length, "station")} · ${scopeName}`
            : `${plural(data.stations.length, "station")} · ${data.waterbodies.length} waterbodies`}
        </span>
      </div>

      <Tabs pane={pane} onPane={setPane} trendLabel="Chemistry" />

      <main className={"body" + (selected || activeWb ? " has-detail" : "")}>
        <section className={"pane pane-list" + (pane === "list" ? " show" : "")}>
          <StationList
            stations={inScope}
            param={param}
            terciles={terciles}
            selectedId={view.stationId}
            onSelect={view.selectStation}
            scopeName={scopeName}
          />
        </section>

        <section className={"pane pane-map" + (pane === "map" ? " show" : "")}>
          <LazyMap
            points={points}
            selectedId={view.stationId}
            fitTo={fitTo}
            basemap={basemap}
            token={data.mapboxToken}
            onSelect={view.selectStation}
          />
        </section>

        <section className={"pane pane-chart" + (pane === "trend" ? " show" : "")}>
          <div className="chart-head">
            <h2>{chartTitle}</h2>
            {!selected && <span className="chart-hint">Select a station to filter</span>}
          </div>

          <div className="metric-tabs" role="tablist" aria-label="Parameter">
            {PARAMS.filter((p) => (data.coverage[p.key] ?? 0) > 0).map((p) => (
              <button
                key={p.key}
                role="tab"
                aria-selected={p.key === param.key}
                className={p.key === param.key ? "on" : ""}
                onClick={() => view.setParam("param", p.key === DEFAULT_PARAM ? null : p.key)}
              >
                {p.short}
              </button>
            ))}
          </div>

          <TrendChart
            series={series}
            thresholds={thresholds}
            yLabel={param.unit || param.short}
            format={(v) => formatValue(v, param.digits)}
            emptyMessage={
              depths.length === 0
                ? "No depth layers selected — turn one on below"
                : available.length === 0
                  ? `No ${param.label.toLowerCase()} recorded here`
                  : `No ${param.label.toLowerCase()} at the selected depth${depths.length === 1 ? "" : "s"}`
            }
          />

          <div className="chart-foot">
            {/* The legend is the control. Clicking a depth shows or hides its
                line, which is where people reach for it anyway. Depths with
                no data at this parameter stay visible but inert, so the
                absence is legible rather than a silently missing entry. */}
            <span className="depth-keys" role="group" aria-label="Depth layers">
              {DEPTHS.map((d) => {
                const has = available.includes(d);
                const on = depths.includes(d);
                const isLast = on && depths.length === 1;
                return (
                  <button
                    key={d}
                    type="button"
                    className={
                      "chart-key depth-key" + (on && has ? " on" : "") +
                      (has ? "" : " empty") + (isLast ? " locked" : "")
                    }
                    onClick={() => has && toggleDepth(d)}
                    disabled={!has}
                    aria-pressed={has ? on : undefined}
                    title={
                      !has
                        ? `No ${param.label.toLowerCase()} recorded at ${d.toLowerCase()} here`
                        : isLast
                          ? "The last visible depth cannot be hidden"
                          : on
                            ? `Hide the ${d.toLowerCase()} line`
                            : `Show the ${d.toLowerCase()} line`
                    }
                  >
                    <i style={{ background: on && has ? DEPTH_COLOR[d] : "transparent",
                                borderColor: DEPTH_COLOR[d] }} />
                    {d}
                  </button>
                );
              })}
            </span>
            {param.judgement === "relative" && (
              <span className="chart-key chart-caution">
                No applicable standard — colours rank{" "}
                {tercileInfo?.basis === "all" && activeWb
                  ? `against all ${tercileInfo.n} stations`
                  : `the ${tercileInfo?.n ?? 0} stations shown`}
                , they do not grade them
              </span>
            )}
            <span className="count">{plural(pointCount, "point")}</span>
          </div>
        </section>

        {selected && (
          <section className={"pane pane-detail show" + (pane === "map" ? " show-narrow" : "")}>
            <StationDetail
              station={selected}
              rows={data.samples.filter((r) => r.s === selected.key)}
              param={param}
              terciles={terciles}
              tercileBasis={tercileInfo?.basis ?? null}
              tercileN={tercileInfo?.n ?? 0}
              url={view.permalink}
              waterbodyLink={view.groupLink(selected.waterbody)}
              onClose={() => view.selectStation(null)}
              onWaterbody={view.selectGroup}
              onParam={(k) => view.setParam("param", k === DEFAULT_PARAM ? null : k)}
            />
          </section>
        )}

        {!selected && activeWb && (
          <section
            className={"pane pane-detail is-group show" + (pane === "map" ? " show-narrow" : "")}
          >
            <div className="detail">
              <h2 className="detail-name">{activeWb}</h2>
              <p className="detail-sub">
                {plural(inScope.length, "monitoring station")}
                {(() => {
                  const ys = inScope.flatMap((s) => [s.firstYear, s.lastYear])
                    .filter((y): y is number => y !== null);
                  return ys.length ? ` · ${Math.min(...ys)}–${Math.max(...ys)}` : "";
                })()}
              </p>
              <dl className="stats">
                <div>
                  <dt>Stations</dt>
                  <dd>{inScope.length}</dd>
                </div>
                <div>
                  <dt>Samples</dt>
                  <dd>{inScope.reduce((a, s) => a + s.samples, 0).toLocaleString()}</dd>
                </div>
                <div>
                  <dt>Mean {param.short}</dt>
                  <dd>
                    {formatValue(
                      mean(inScope.map((s) => s.latest[param.key]?.v ?? null)),
                      param.digits,
                    )}
                    {param.unit && <span className="unit">{param.unit}</span>}
                  </dd>
                </div>
              </dl>
              <ShareCard
                url={view.groupLink(activeWb)}
                filename={activeWb.replace(/\W+/g, "-").toLowerCase()}
                heading={`Link to ${activeWb}`}
                blurb="Opens this dashboard filtered to this waterbody — for a partner page, a newsletter or a sign."
              />
            </div>
          </section>
        )}
      </main>

      <Footer
        items={[
          {
            label: "Updated",
            value: new Date(data.generatedAt).toLocaleString(undefined, {
              month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
            }),
          },
          { label: "Stations", value: String(data.stations.length) },
          { label: "Waterbodies", value: String(data.waterbodies.length) },
          {
            label: "Samples",
            value: data.samples.length.toLocaleString(),
            note: data.rowsExcluded ? `+${data.rowsExcluded} unlinked` : undefined,
            title: data.rowsExcluded
              ? `${data.rowsExcluded} samples in Airtable have no station link, date or depth class and cannot be placed.`
              : undefined,
          },
        ]}
        credit={<WaterWatchCredit />}
        right={
          <a href="https://watershedcouncil.org" target="_blank" rel="noreferrer">
            watershedcouncil.org
          </a>
        }
      />
    </div>
  );
}
